import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import BaseChart from "../charts/BaseChart";
import type { KpiPoint } from "../../services/ShiftAnalysisService";
import Panel from "./Panel";

interface Props {
  points: KpiPoint[];
}

/** HH:MM (wall-clock) from a UTC-anchored epoch (see parseKpiPoint). */
function hm(t: number): string {
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * "KPI over shift" — line-average efficiency and utilization across the shift
 * (from production_period), rendered with the app's standard BaseChart. Values
 * are shown as-is (they can be negative or far above 100%); the Y range is
 * computed from the data so nothing is clipped or hidden.
 */
const KpiTimeline: React.FC<Props> = ({ points }) => {
  const { t } = useTranslation();

  const hasData = points.some(
    (p) => p.efficiency != null || p.utilization != null,
  );

  const { categories, effData, utilData, yMin, yMax, avg } = useMemo(() => {
    const categories = points.map((p) => hm(p.t));
    const toPct = (v: number | null) =>
      v == null ? null : Number((v * 100).toFixed(1));
    const effData = points.map((p) => toPct(p.efficiency));
    const utilData = points.map((p) => toPct(p.utilization));

    const all = [...effData, ...utilData].filter((v): v is number => v != null);
    // Keep the 0% baseline in view and never clip negatives/outliers.
    const lo = all.length ? Math.min(0, ...all) : 0;
    const hi = all.length ? Math.max(...all) : 100;

    const effVals = effData.filter((v): v is number => v != null);
    const avg = effVals.length
      ? effVals.reduce((a, b) => a + b, 0) / effVals.length
      : null;

    return {
      categories,
      effData,
      utilData,
      yMin: Math.floor(lo),
      yMax: Math.ceil(hi),
      avg,
    };
  }, [points]);

  return (
    <Panel
      label={t("kpiOverShift", "KPI over shift")}
      meta={t("efficiencyUtilization", "efficiency & utilization")}
      right={
        avg != null ? (
          <span
            className="text-xs tabular-nums"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("avgEff", "avg eff")}{" "}
            <span
              className="font-semibold"
              style={{ color: "var(--text-primary)" }}
            >
              {Math.round(avg)}%
            </span>
          </span>
        ) : undefined
      }
    >
      {!hasData ? (
        <div
          className="py-10 text-center text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("noKpiData", "No KPI curve available for this shift.")}
        </div>
      ) : (
        <BaseChart
          type="line"
          height={300}
          categories={categories}
          series={[
            {
              name: t("efficiency", "Efficiency"),
              data: effData,
              color: "#10b981",
            },
            {
              name: t("utilization", "Utilization"),
              data: utilData,
              color: "#3b82f6",
            },
          ]}
          yAxis={{ format: "{value}%", min: yMin, max: yMax }}
          legend={{ enabled: true, position: "top" }}
          tooltip={{ suffix: "%" }}
          exporting={{ enabled: true, filename: "kpi-over-shift" }}
          noDataMessage={t(
            "noKpiData",
            "No KPI curve available for this shift.",
          )}
        />
      )}
    </Panel>
  );
};

export default KpiTimeline;
